import React, { useEffect, useState } from 'react';
import { palette, Paper, Typography } from '@dev/ee-components';
import { Grid } from '@material-ui/core';
import { find, findIndex } from 'lodash-es';
import { DEFAULT_OPENED_ONBOARDING_TASK_ID } from '@/services/constants';
import useMobile from '@/services/hooks/useMobile';
import {
    OnboardingCardHeaderWrapper,
    OnboardingCardStyledCollapse,
    OnboardingCardStyledSteps,
    OnboardingCardTitle,
    OnboardingCardWithTopBorder,
} from './styles';
import { OnboardingTaskMarkup } from './components/OnboardingTaskMarkup';
import { OnboardingCardStep } from '../OnboardingCardStep';
import { OnboardingCardProps } from '../../types';
import { RadioTaskSelect } from './components/RadioTaskSelect';
import { getFirstNotDoneStepIndex } from '../../tools/getFirstNotDoneStepIndex';

export const OnboardingCard = (props: OnboardingCardProps) => {
    const isMobile = useMobile();
    const {
        title,
        steps,
        header,
        description,
        activeStepsIndex,
        icon,
        cardIndex,
        openedCardIndex,
        handleOpenedCard,
        openedStep,
        isFirstNotDoneTask,
    } = props;
    const [selectedStepsVariant, setSelectedStepsVariant] = useState(steps[activeStepsIndex]?.id);
    const [openedStepIndex, setOpenedStepIndex] = useState(openedStep);

    useEffect(() => {
        setOpenedStepIndex(openedStep);
    }, [openedStep]);

    const isOpen = cardIndex === openedCardIndex;
    const isRadioGroup = steps.length > 1;

    const toggleOpen = (): void => {
        const newOpenedTaskIndex = cardIndex === openedCardIndex ? DEFAULT_OPENED_ONBOARDING_TASK_ID : cardIndex;
        handleOpenedCard(newOpenedTaskIndex);
    };

    const getSelectedStepsIndexById = (id: string): number => findIndex(steps, (step) => step.id === id);

    const handleSetSelectedStepsVariant = (id: string): void => {
        setSelectedStepsVariant(id);
        find(steps, (step) => step.id === id)?.saveAsActive?.();

        if (isFirstNotDoneTask) {
            const firstNotDoneStep = getFirstNotDoneStepIndex(steps?.[getSelectedStepsIndexById(id)]);
            setOpenedStepIndex(firstNotDoneStep);
        }
    };

    const selectedStepsIndex = getSelectedStepsIndexById(selectedStepsVariant);
    const selectedSteps = steps?.[selectedStepsIndex];
    const selectedStepsItems = selectedSteps?.items;
    const selectedStepsItemsCount = selectedStepsItems?.length ?? 0;
    const completedSteps = selectedStepsItems?.filter((item) => item.isCompleted || item.isSkipped)?.length ?? 0;
    const taskCompleted = completedSteps === selectedStepsItemsCount;

    return (
        <Paper>
            <OnboardingCardHeaderWrapper
                $isOpen={isOpen}
                container
                onClick={toggleOpen}
                $isTaskCompleted={taskCompleted}
                $isMobile={isMobile}
            >
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ borderBottom: isOpen && `1px solid ${palette.gray[3]}` }}
                >
                    <OnboardingCardTitle>
                        {icon}
                        <Typography variant="h2">{header}</Typography>
                    </OnboardingCardTitle>
                    <OnboardingTaskMarkup doneSteps={completedSteps} allSteps={selectedStepsItemsCount} />
                </Grid>
            </OnboardingCardHeaderWrapper>
            <OnboardingCardStyledCollapse in={isOpen} $isMobile={isMobile}>
                <OnboardingCardWithTopBorder>
                    <Grid container direction="column" style={{ marginBottom: 48, gap: 12 }}>
                        <Typography variant="h1">{title}</Typography>
                        <Typography variant="body1">{description}</Typography>
                    </Grid>
                    <OnboardingCardStyledSteps>
                        {isRadioGroup && (
                            <RadioTaskSelect
                                title={title}
                                steps={steps}
                                selectedStepsVariantValue={selectedSteps?.id}
                                handleSetSelectedStepsVariant={handleSetSelectedStepsVariant}
                            />
                        )}
                        {selectedStepsItems?.map((step, index) => (
                            <OnboardingCardStep
                                key={`${step.title}-${index}`}
                                {...step}
                                isActive={openedStepIndex === index}
                                selectedStepsIndex={selectedStepsIndex}
                                no={index + 1}
                            />
                        ))}
                    </OnboardingCardStyledSteps>
                </OnboardingCardWithTopBorder>
            </OnboardingCardStyledCollapse>
        </Paper>
    );
};
